import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { connApi, spaceApi } from '../services/api';
import { useSpaceId } from '../contexts/SpaceContext';
import type { CreateGoogleWatchInput } from '../types/google';

/** Org Google connections (Gmail/Chat). Admin-set-up, read by the connect UI. */
export function useGoogleConnections() {
  return useQuery({
    queryKey: ['conn', 'google', 'connections'],
    queryFn: () => connApi.google.listConnections(),
    staleTime: 5 * 60 * 1000,
  });
}

/** Watches under a connection that route to the current space. */
export function useGoogleWatches(connectionId: string | undefined) {
  const spaceId = useSpaceId();
  return useQuery({
    queryKey: ['space', spaceId, 'google', 'watches', connectionId],
    queryFn: () => spaceApi(spaceId).google.listWatches(connectionId!),
    enabled: !!connectionId,
  });
}

/**
 * Calendar only: list the calendars visible to the connecting Google account
 * (primary + secondary + shared) so the user can pick one to route into the
 * current space. Re-fetches when the chosen account/principal changes.
 */
export function useListGoogleCalendars(
  connectionId: string | undefined,
  accountId?: string,
  principalKind?: 'user' | 'space-service',
) {
  const spaceId = useSpaceId();
  return useQuery({
    queryKey: ['space', spaceId, 'google', 'calendars', connectionId, accountId, principalKind],
    queryFn: () =>
      spaceApi(spaceId).google.listCalendars(connectionId!, { accountId, principalKind }),
    enabled: !!connectionId,
  });
}

export function useCreateGoogleWatch(connectionId: string | undefined) {
  const spaceId = useSpaceId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateGoogleWatchInput) =>
      spaceApi(spaceId).google.connect(connectionId!, input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['space', spaceId, 'google', 'watches', connectionId],
      });
    },
  });
}

export function useDeleteGoogleWatch(connectionId: string | undefined) {
  const spaceId = useSpaceId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (watchId: string) =>
      spaceApi(spaceId).google.deleteWatch(connectionId!, watchId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['space', spaceId, 'google', 'watches', connectionId],
      });
    },
  });
}

/** Admin: org-wide watch list under a connection. */
export function useOrgGoogleWatches(connectionId: string | undefined) {
  return useQuery({
    queryKey: ['conn', 'google', 'org-watches', connectionId],
    queryFn: () => connApi.google.listOrgWatches(connectionId!),
    enabled: !!connectionId,
  });
}

/**
 * Detect the admin's Chat bot DMs as `chatAppId` candidates. Button-triggered
 * (mutation) since it makes several Google API calls per click.
 */
export function useDetectChatApp() {
  return useMutation({
    mutationFn: (accountId?: string) => connApi.google.detectChatApp(accountId),
  });
}

export function useCreateGoogleConnection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: import('../types/google').CreateGoogleConnectionInput) =>
      connApi.google.createConnection(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conn', 'google', 'connections'] });
    },
  });
}

/**
 * Delete an org Google connection. These are org integrations
 * (`ingestionType: 'google-push'`), so removal goes through the shared org
 * integrations delete route.
 */
export function useDeleteGoogleConnection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (integrationId: string) =>
      connApi.org.integrations.delete(integrationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conn', 'google', 'connections'] });
    },
  });
}
